import { REDUCED_MOTION } from '@utils/Motion';
import { gsap } from 'gsap';

// Must match the 500ms cubic-bezier(0.16, 1, 0.3, 1) used by the pill morph in header.css
const DURATION = 0.5;
const WIDTH_EASE = 'expo.out';
const FADE_EASE = 'power1.out';
// Outgoing label fades out over the first portion; the incoming label starts fading in
// before it has gone so the two overlap and the link is never blank. The swap happens
// at the same moment for every link regardless of length.
const FADE_OUT_END = 0.4;
const FADE_IN_START = 0.15;

interface NavTextPair {
    container: HTMLElement;
    full: HTMLElement;
    short: HTMLElement;
}

function readTextWidth(el: HTMLElement) {
    return parseFloat(getComputedStyle(el).getPropertyValue('--text-width'));
}

/**
 * Replaces CSS text crossfade with GSAP tweens.
 * Call AFTER measureNavTextWidths() has set --text-width on spans.
 *
 * Stacks both labels in the same space. The outgoing label fades out first,
 * then the incoming label fades in, while the container width eases in step
 * with the rest of the header morph. Each direction gets a fresh ease-out
 * tween (rather than reversing a timeline) so it matches the CSS transitions.
 */
export function initNavCrossfadeGsap(header: HTMLElement) {
    header.setAttribute('data-gsap-nav', '');

    const duration = REDUCED_MOTION ? 0.01 : DURATION;
    const fadeOutDuration = duration * FADE_OUT_END;
    const fadeInDelay = duration * FADE_IN_START;
    const fadeInDuration = duration - fadeInDelay;

    const pairs: NavTextPair[] = [];
    let scrolled = false;

    const containers = header.querySelectorAll<HTMLElement>(
        '[data-has-short-title], .header__btn-text',
    );

    containers.forEach(container => {
        const full = container.querySelector<HTMLElement>('.nav-text-full');
        const short = container.querySelector<HTMLElement>('.nav-text-short');

        if (!full || !short) return;

        gsap.set(container, { position: 'relative', overflow: 'clip', width: readTextWidth(full) });
        gsap.set(full, { maxWidth: 'none', overflow: 'visible' });
        // No `top`: the static position keeps it level with the full label inside the link's padding
        gsap.set(short, {
            position: 'absolute',
            left: 0,
            maxWidth: 'none',
            overflow: 'visible',
            opacity: 0,
            visibility: 'visible',
        });

        pairs.push({ container, full, short });
    });

    function setScrolled(isScrolled: boolean) {
        scrolled = isScrolled;

        pairs.forEach(({ container, full, short }) => {
            const outgoing = isScrolled ? full : short;
            const incoming = isScrolled ? short : full;

            gsap.to(container, {
                width: readTextWidth(incoming),
                duration,
                ease: WIDTH_EASE,
                overwrite: 'auto',
            });
            gsap.to(outgoing, { opacity: 0, duration: fadeOutDuration, ease: FADE_EASE, overwrite: 'auto' });
            gsap.to(incoming, {
                opacity: 1,
                duration: fadeInDuration,
                delay: fadeInDelay,
                ease: FADE_EASE,
                overwrite: 'auto',
            });
        });
    }

    return {
        play() {
            setScrolled(true);
        },

        reverse() {
            setScrolled(false);
        },

        remeasure() {
            pairs.forEach(({ container, full, short }) => {
                gsap.set(container, { width: readTextWidth(scrolled ? short : full) });
            });
        },
    };
}
